import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ShieldCheck, Lock, Eye, Database, Clock, Globe, Users, Cookie, Mail } from 'lucide-react';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0d0e12] text-white flex flex-col font-sans selection:bg-[#FF5C00]/30">
      <header className="py-6 px-4 md:px-8 max-w-7xl mx-auto w-full flex items-center justify-between z-10 relative">
        <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/')}>
          <Logo />
        </div>
        <button onClick={() => navigate(-1)} className="flex items-center text-sm font-bold text-gray-400 hover:text-white transition-colors">
          <ChevronLeft className="w-4 h-4 mr-1" />
          Retour
        </button>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 md:px-8 py-12">
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center justify-center p-3 bg-[#FF5C00]/10 rounded-full mb-4">
            <ShieldCheck className="w-8 h-8 text-[#FF5C00]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Politique de Confidentialité</h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Vos données personnelles sont traitées avec soin et conformément au Règlement Général sur la Protection des Données (RGPD — UE 2016/679).
          </p>
          <p className="text-sm text-gray-500">Dernière mise à jour : 18 juin 2026</p>
        </div>

        <div className="space-y-8">

          {/* 1. Responsable du traitement */}
          <section className="bg-[#121212] border border-white/10 rounded-3xl p-8 md:p-10">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-3">
              <Users className="w-5 h-5 text-[#FF5C00] shrink-0" />
              1. Responsable du traitement
            </h2>
            <div className="text-gray-300 leading-relaxed space-y-2">
              <p><span className="font-semibold text-white">Festin</span> — plateforme de mise en relation entre organisateurs de festivals et exposants.</p>
              <p>Contact : <a href="mailto:contact@festin.live" className="text-[#FF5C00] hover:underline">contact@festin.live</a></p>
              <p className="text-sm text-gray-500">Pour toute demande relative à vos données personnelles, utilisez l'adresse ci-dessus.</p>
            </div>
          </section>

          {/* 2. Données collectées */}
          <section className="bg-[#121212] border border-white/10 rounded-3xl p-8 md:p-10">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-3">
              <Database className="w-5 h-5 text-[#FF5C00] shrink-0" />
              2. Données collectées et finalités
            </h2>
            <div className="text-gray-300 leading-relaxed space-y-4">
              <p>Nous collectons uniquement les données nécessaires au fonctionnement de la plateforme :</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 pr-4 text-white font-bold">Traitement</th>
                      <th className="text-left py-2 pr-4 text-white font-bold">Données concernées</th>
                      <th className="text-left py-2 text-white font-bold">Base légale</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-200">Inscription / Authentification</td>
                      <td className="py-3 pr-4 text-gray-400">Nom, email, mot de passe (haché), rôle</td>
                      <td className="py-3 text-gray-400">Exécution du contrat</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-200">Profil exposant / festival</td>
                      <td className="py-3 pr-4 text-gray-400">Nom commercial, description, logo, bannière, coordonnées, documents (KBIS, assurance)</td>
                      <td className="py-3 text-gray-400">Exécution du contrat</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-200">Candidatures</td>
                      <td className="py-3 pr-4 text-gray-400">Données de candidature, statut, historique</td>
                      <td className="py-3 text-gray-400">Exécution du contrat</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-200">Paiements</td>
                      <td className="py-3 pr-4 text-gray-400">Montant, référence Stripe, statut (aucune donnée bancaire stockée par Festin)</td>
                      <td className="py-3 text-gray-400">Obligation légale / contrat</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-200">Messagerie</td>
                      <td className="py-3 pr-4 text-gray-400">Contenu des messages entre utilisateurs</td>
                      <td className="py-3 text-gray-400">Exécution du contrat</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-200">Notifications</td>
                      <td className="py-3 pr-4 text-gray-400">Alertes liées à l'activité (candidatures, paiements)</td>
                      <td className="py-3 text-gray-400">Intérêt légitime</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 font-semibold text-gray-200">Contrats PDF</td>
                      <td className="py-3 pr-4 text-gray-400">Nom, festival, montant, référence paiement</td>
                      <td className="py-3 text-gray-400">Obligation légale (archives comptables)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* 3. Sous-traitants */}
          <section className="bg-[#121212] border border-white/10 rounded-3xl p-8 md:p-10">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-3">
              <Globe className="w-5 h-5 text-[#FF5C00] shrink-0" />
              3. Sous-traitants et transferts
            </h2>
            <div className="text-gray-300 leading-relaxed space-y-4">
              <p>Nous faisons appel aux sous-traitants suivants, chacun encadré par un accord de traitement de données :</p>
              <div className="space-y-3">
                {[
                  {
                    name: 'Google Firebase / Cloud Run',
                    role: 'Authentification, base de données (Firestore), stockage de fichiers, hébergement',
                    location: '🇪🇺 europe-west1 (Belgique) — données hébergées dans l\'UE',
                    safe: true,
                  },
                  {
                    name: 'Stripe',
                    role: 'Traitement des paiements en ligne',
                    location: '🇺🇸 États-Unis — encadré par les Clauses Contractuelles Types (SCC) de la Commission Européenne',
                    safe: true,
                  },
                  {
                    name: 'Anthropic (Claude AI)',
                    role: 'Extraction automatique de données festival (parsing de pages web)',
                    location: '🇺🇸 États-Unis — encadré par les Clauses Contractuelles Types (SCC)',
                    safe: true,
                  },
                  {
                    name: 'Sentry',
                    role: 'Monitoring des erreurs techniques (logs anonymisés)',
                    location: '🇺🇸 États-Unis — encadré par les Clauses Contractuelles Types (SCC)',
                    safe: true,
                  },
                ].map(s => (
                  <div key={s.name} className="bg-white/5 rounded-2xl p-4">
                    <p className="font-bold text-white">{s.name}</p>
                    <p className="text-sm text-gray-400 mt-1">{s.role}</p>
                    <p className="text-xs text-gray-500 mt-1">{s.location}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 4. Durée de conservation */}
          <section className="bg-[#121212] border border-white/10 rounded-3xl p-8 md:p-10">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-3">
              <Clock className="w-5 h-5 text-[#FF5C00] shrink-0" />
              4. Durée de conservation
            </h2>
            <div className="text-gray-300 leading-relaxed space-y-3">
              <div className="bg-white/5 rounded-2xl p-4 space-y-2 text-sm">
                <p><span className="font-semibold text-white">Données de compte actif</span> — conservées pendant toute la durée de la relation contractuelle.</p>
                <p><span className="font-semibold text-white">Données comptables et contrats</span> — archivées 10 ans conformément à l'obligation légale (art. L123-22 du Code de commerce).</p>
                <p><span className="font-semibold text-white">Logs techniques (Sentry)</span> — 90 jours.</p>
                <p><span className="font-semibold text-white">Suppression de compte</span> — toutes les données personnelles actives sont effacées immédiatement. Les archives comptables restent pour la durée légale obligatoire.</p>
              </div>
            </div>
          </section>

          {/* 5. Cookies */}
          <section className="bg-[#121212] border border-white/10 rounded-3xl p-8 md:p-10">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-3">
              <Cookie className="w-5 h-5 text-[#FF5C00] shrink-0" />
              5. Cookies
            </h2>
            <div className="text-gray-300 leading-relaxed space-y-3 text-sm">
              <p>Festin utilise uniquement des <span className="font-semibold text-white">cookies techniques strictement nécessaires</span> au fonctionnement de la plateforme :</p>
              <div className="bg-white/5 rounded-2xl p-4 space-y-2">
                <p><span className="font-semibold text-white">Session Firebase</span> — maintient votre connexion (authentification). Nécessaire, exempt de consentement.</p>
                <p><span className="font-semibold text-white">Préférences locales</span> — stockage de vos choix d'interface (thème, consentement cookies) via <code className="bg-white/10 px-1 rounded">localStorage</code>.</p>
              </div>
              <p>Aucun cookie publicitaire, de tracking ou analytique tiers n'est déposé sur votre appareil.</p>
            </div>
          </section>

          {/* 6. Vos droits */}
          <section className="bg-[#121212] border border-white/10 rounded-3xl p-8 md:p-10">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-3">
              <Lock className="w-5 h-5 text-[#FF5C00] shrink-0" />
              6. Vos droits
            </h2>
            <div className="text-gray-300 leading-relaxed space-y-3">
              <p>Conformément au RGPD (art. 15 à 22), vous disposez des droits suivants :</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {[
                  { title: 'Droit d\'accès', desc: 'Obtenir une copie de vos données' },
                  { title: 'Droit de rectification', desc: 'Corriger vos données inexactes' },
                  { title: 'Droit à l\'effacement', desc: 'Supprimer votre compte et vos données depuis Paramètres' },
                  { title: 'Droit à la portabilité', desc: 'Recevoir vos données dans un format structuré' },
                  { title: 'Droit d\'opposition', desc: 'Vous opposer à certains traitements' },
                  { title: 'Droit de réclamation', desc: 'Saisir la CNIL : cnil.fr' },
                ].map(r => (
                  <div key={r.title} className="bg-white/5 rounded-xl p-3">
                    <p className="font-bold text-white">{r.title}</p>
                    <p className="text-gray-400 mt-0.5">{r.desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-400 pt-2">
                Pour exercer vos droits : <a href="mailto:contact@festin.live" className="text-[#FF5C00] hover:underline font-semibold">contact@festin.live</a> — réponse sous 30 jours.
              </p>
            </div>
          </section>

          {/* 7. Sécurité */}
          <section className="bg-[#121212] border border-white/10 rounded-3xl p-8 md:p-10">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-3">
              <Eye className="w-5 h-5 text-[#FF5C00] shrink-0" />
              7. Sécurité
            </h2>
            <div className="text-gray-300 leading-relaxed space-y-2 text-sm">
              <p>Les données sont transmises via HTTPS (TLS). Les mots de passe sont hashés par Firebase Authentication. Aucune donnée bancaire n'est stockée par Festin — les paiements transitent exclusivement par Stripe (certifié PCI-DSS niveau 1).</p>
              <p>Les accès aux données sont restreints aux seuls services techniques nécessaires à la fourniture de la plateforme.</p>
            </div>
          </section>

          {/* Contact */}
          <section className="bg-[#FF5C00]/5 border border-[#FF5C00]/20 rounded-3xl p-8 text-center">
            <Mail className="w-6 h-6 text-[#FF5C00] mx-auto mb-3" />
            <h2 className="text-lg font-bold mb-2">Une question sur vos données ?</h2>
            <p className="text-gray-400 text-sm mb-4">Nous vous répondons sous 30 jours ouvrés.</p>
            <a
              href="mailto:contact@festin.live"
              className="inline-block bg-[#FF5C00] text-white font-black px-6 py-3 rounded-2xl hover:bg-[#ff6b1a] transition-colors"
            >
              contact@festin.live
            </a>
          </section>

        </div>
      </main>
      <Footer />
    </div>
  );
}
